import type {
  SalesHourlyBucket,
  SalesSummary
} from '@/types/SalesSummary.interface';
import { BUSINESS_DAY_START_HOUR } from '@/utils/businessDay';
import {
  addDays,
  averageCard,
  EntryMode,
  ReportPreset
} from '@/utils/salesReport';

/**
 * "Önceki döneme göre" rozetleri (yalnız admin Satış Raporu). Web POS ile
 * ortak `salesReport.ts`'e dokunmaz; onun saf yardımcılarını kullanır.
 */

export interface ReportWindow {
  startDate: string;
  endDate: string;
}

/** Kapsayıcı gün sayısı (`YYYY-MM-DD`, UTC takvim aritmetiği). */
export function dayCount({ startDate, endDate }: ReportWindow): number {
  const toUtc = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((toUtc(endDate) - toUtc(startDate)) / 86_400_000) + 1;
}

/** Hemen önceki, aynı uzunluktaki aralık: bugün → dün, son 7 gün → önceki 7 gün. */
export function previousWindow(range: ReportWindow): ReportWindow {
  const days = dayCount(range);
  const endDate = addDays(range.startDate, -1);
  return { startDate: addDays(endDate, -(days - 1)), endDate };
}

/** Yüzde değişim; önceki dönem 0 ise anlamsız olduğundan `null`. */
export function percentChange(
  current: number,
  previous: number
): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/** Saatin işletme günündeki sırası: 07:00 → 0, 23:00 → 16, 02:00 → 19. */
export const businessHourIndex = (hour: number): number =>
  (((hour - BUSINESS_DAY_START_HOUR) % 24) + 24) % 24;

/** İşletme günü sırasıyla `throughHour` dahil o saate kadarki satış ve işlem. */
export function hourlyTotalsThrough(
  hourly: SalesHourlyBucket[],
  throughHour: number
): { sales: number; transactions: number } {
  const limit = businessHourIndex(throughHour);
  return hourly.reduce(
    (sum, bucket) =>
      businessHourIndex(bucket.hour) <= limit
        ? {
            sales: sum.sales + bucket.sales,
            transactions: sum.transactions + bucket.transactions
          }
        : sum,
    { sales: 0, transactions: 0 }
  );
}

export interface MetricComparison {
  /** Yüzde değişim; gösterilemiyorsa `null`. */
  change: number | null;
  previousValue: number;
}

export interface PeriodComparison {
  previous: ReportWindow;
  totalSales: MetricComparison;
  average: MetricComparison;
  /**
   * "Bugün" dünün AYNI SAATİNE kadarki satışla kıyaslanır (gün bitmeden
   * dünün tamamıyla kıyas hep düşüş gösterirdi). Ortalama tam günle kıyaslanır.
   */
  partialThroughHour: number | null;
}

/**
 * İki özetin kartlardaki değerlerini kıyaslar. `today` seçiliyse toplam satış
 * dünün saatlik kovalarından şu anki saate kadar toplanır.
 */
export function compareSummaries(
  current: SalesSummary,
  previous: SalesSummary,
  options: {
    entryMode: EntryMode;
    preset: ReportPreset;
    previousWindow: ReportWindow;
    nowHour: number;
  }
): PeriodComparison {
  const partial = options.preset === 'today';
  const previousTotal = partial
    ? hourlyTotalsThrough(previous.hourly, options.nowHour).sales
    : previous.totals.totalSales;
  const currentAverage = averageCard(current, options.entryMode).value;
  const previousAverage = averageCard(previous, options.entryMode).value;

  return {
    previous: options.previousWindow,
    totalSales: {
      change: percentChange(current.totals.totalSales, previousTotal),
      previousValue: previousTotal
    },
    average: {
      change: percentChange(currentAverage, previousAverage),
      previousValue: previousAverage
    },
    partialThroughHour: partial ? options.nowHour : null
  };
}

/** Rozet metni: "%12,4" (işaret ok ile gösterilir). */
export const formatChange = (change: number): string =>
  `%${Math.abs(change).toLocaleString('tr-TR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })}`;

/** ±%0,05'in altı "değişmedi" sayılır (yuvarlanınca %0,0). */
export const changeDirection = (change: number): 'up' | 'down' | 'flat' =>
  Math.abs(change) < 0.05 ? 'flat' : change > 0 ? 'up' : 'down';

/** Kıyas etiketi anahtarı: "Önceki 7 güne göre" gibi. */
export const compareLabelKey = (preset: ReportPreset): string =>
  `salesReport.compare.${preset}`;
